/**
 * Probe 진행 관측의 주기 정책.
 *
 * BE 에 push · SSE · callback 이 없어 `GET /v1/probes/{uuid}` 폴링이 유일한 관측 수단이다.
 * 간격은 RTT(250ms)의 수 배로 두고(규칙 §6), 오래 걸리는 수집에서는 더 늘린다 — 몇 분짜리
 * 작업에 3초 폴링을 계속 유지하면 얻는 것 없이 요청만 쌓인다.
 *
 * Owns:
 * - 상태 · 경과 시간 → 다음 간격(또는 중지), 장기 수집 안내의 임계.
 *
 * Does not own:
 * - 타이머 · 세대 · 문서 가시성 — `probePoller.ts`. 조회 — `api/probeApi.ts`.
 */
import { isProbeOpen, type ProbeState } from "../domain/probe";

/** 접수 직후. Worker 대기열 폴링이 5초라 첫 전이는 이보다 늦을 수 있다. */
export const PROBE_POLL_INTERVAL_MS = 3_000;

/** 오래 걸리는 수집의 간격. Worker `SPNP_COLLECT_POLL_SECONDS` 와 같은 리듬. */
export const PROBE_POLL_SLOW_INTERVAL_MS = 5_000;

/** 이 시간이 지나면 느린 간격으로 내린다. */
export const PROBE_POLL_SLOWDOWN_AFTER_MS = 60_000;

/**
 * 이 시간을 넘기면 "예상보다 길어지고 있다" 는 안내를 더한다.
 *
 * 내부 재시도(3회 · `30초 × attempt` backoff)와 lease 회수(120초) 때문에 정상 작업도 몇 분간
 * `collecting` 에 머문다 — **정체를 오류로 표시하지 않는다.**
 */
export const PROBE_SLOW_NOTICE_AFTER_MS = 180_000;

/** 다음 조회까지의 간격. 종료 상태면 `null`(더 조회하지 않는다). */
export function probePollIntervalMs(state: ProbeState, elapsedMs: number): number | null {
  if (!isProbeOpen(state)) {
    return null;
  }
  return elapsedMs >= PROBE_POLL_SLOWDOWN_AFTER_MS
    ? PROBE_POLL_SLOW_INTERVAL_MS
    : PROBE_POLL_INTERVAL_MS;
}

export function isProbeTakingLong(state: ProbeState, elapsedMs: number): boolean {
  return isProbeOpen(state) && elapsedMs >= PROBE_SLOW_NOTICE_AFTER_MS;
}
